export type PathSelectorQuestion = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly required?: boolean;
  readonly multi?: boolean;
  readonly options: readonly PathSelectorOption[];
};

export type PathSelectorOption = {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly recommendationScores: Readonly<Record<string, number>>;
};

export type PathRecommendation = {
  readonly id: string;
  readonly label: string;
  readonly summary: string;
  readonly recommendedPath?: string;
  readonly recommendedServiceLine?: string;
  readonly ctaLabel?: string;
  readonly href?: string;
};

export type PathSelectorSchema = {
  readonly id: string;
  readonly questions: readonly PathSelectorQuestion[];
  readonly recommendations: readonly PathRecommendation[];
};

export type PathSelectorResponses = Readonly<Record<string, string | readonly string[] | undefined>>;

export type PathSelectorResult = {
  readonly schemaId: string;
  readonly answered: number;
  readonly total: number;
  readonly complete: boolean;
  readonly scores: Readonly<Record<string, number>>;
  readonly recommendation: PathRecommendation;
  readonly rankedRecommendations: readonly PathRecommendation[];
};

const unique = <T>(values: readonly T[]): T[] => [...new Set(values)];

function selectedIds(question: PathSelectorQuestion, responses: PathSelectorResponses): string[] {
  const value = responses[question.id];

  if (question.multi) {
    return Array.isArray(value) ? unique(value.map(String).filter(Boolean)) : [];
  }

  return typeof value === "string" && value ? [value] : [];
}

export function evaluatePathSelector(schema: PathSelectorSchema, responses: PathSelectorResponses): PathSelectorResult {
  const scores = Object.fromEntries(schema.recommendations.map((recommendation) => [recommendation.id, 0])) as Record<string, number>;
  let answered = 0;
  let requiredAnswered = 0;
  const requiredTotal = schema.questions.filter((question) => question.required !== false).length;

  schema.questions.forEach((question) => {
    const ids = selectedIds(question, responses);

    if (ids.length > 0) {
      answered += 1;
      if (question.required !== false) {
        requiredAnswered += 1;
      }
    }

    question.options
      .filter((option) => ids.includes(option.id))
      .forEach((option) => {
        Object.entries(option.recommendationScores).forEach(([recommendationId, score]) => {
          scores[recommendationId] = (scores[recommendationId] ?? 0) + score;
        });
      });
  });

  const rankedRecommendations = [...schema.recommendations].sort((a, b) => (scores[b.id] ?? 0) - (scores[a.id] ?? 0));

  return {
    schemaId: schema.id,
    answered,
    total: schema.questions.length,
    complete: requiredAnswered >= requiredTotal,
    scores,
    recommendation: rankedRecommendations[0] ?? {
      id: "unclassified",
      label: "",
      summary: "",
    },
    rankedRecommendations,
  };
}
